import React from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Block as BlockIcon,
  Pause as PauseIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { needsStoreIntentAction } from '../../utils/workOrderStoreReview';

const MaintenanceWorkflowProgressIndicator = ({ currentStatus, workOrder }) => {
  // Define workflow steps for Maintenance
  const getWorkflowSteps = () => {
    const storeDateGateOpen = Boolean(workOrder?.storeRejectedAt && workOrder?.storeAvailabilityDate);
    const steps = [
      { 
        status: 'SCHEDULED', 
        label: 'Scheduled', 
        description: 'Work order created by CAMO, awaiting CAMO_QM acknowledgment',
        checkField: null // Always shown
      },
      { 
        status: 'CAMO_QM_ACKNOWLEDGED', 
        label: 'CAMO_QM Acknowledged', 
        description: 'CAMO_QM has acknowledged work order creation',
        checkField: 'camoQmAcknowledged'
      },
      { 
        status: 'PLANNING_APPROVED', 
        label: 'Planning Approved', 
        description: 'Planning has reviewed and approved work order',
        checkField: 'planningApproved'
      },
      { 
        status: 'STORE_ACTION', 
        label: 'Store Action', 
        description: 'Store accepts or sets unavailable-parts availability date (if needed)',
        checkField: 'needsStoreAction',
        conditional: true
      },
      {
        status: 'PLANNING_RESCHEDULE_AFTER_STORE',
        label: 'Planning Reschedule',
        description: 'Planning sets maintenance date later than Store availability date',
        checkField: 'storeAvailabilityDate',
        conditional: true
      },
      { 
        status: 'MRO_ACCEPTED', 
        label: 'MRO Accepted', 
        description: 'MRO accepts work order (if needed)',
        checkField: 'needsMROAction',
        conditional: true
      },
      {
        status: 'EXTERNAL_AGENCY_COMPLETED',
        label: 'External Agency Documents',
        description: 'Planning/CAMO upload CRS/CAME docs from external agency (if needed)',
        checkField: 'needsExternalAgencyAction',
        conditional: true
      },
      { 
        status: 'IN_PROGRESS', 
        label: 'In Progress', 
        description: 'Maintenance work in progress',
        checkField: null // Status-based
      },
      { 
        status: 'MRO_ENGINEER_COMPLETED', 
        label: 'MRO_ENGINEER Completed', 
        description: 'MRO_ENGINEER has completed maintenance work',
        checkField: 'mroEngineerCompleted'
      },
      { 
        status: 'MRO_QM_COMPLETION_ACKNOWLEDGED', 
        label: 'MRO QM Completion Acknowledged', 
        description: 'MRO QM has acknowledged completion',
        checkField: 'mroQmCompletionAcknowledged'
      },
      { 
        status: 'CAMO_QM_COMPLETION_ACKNOWLEDGED', 
        label: 'CAMO_QM Completion Acknowledged', 
        description: 'CAMO_QM has acknowledged completion (dual completion)',
        checkField: 'camoQmCompletionAcknowledged'
      },
      { 
        status: 'COMPLETED', 
        label: 'Completed', 
        description: 'Work order completed and ready for maintenance release certificate',
        checkField: null // Status-based
      }
    ];
    
    return steps.filter((step) => {
      if (step.status === 'PLANNING_RESCHEDULE_AFTER_STORE') {
        return storeDateGateOpen;
      }
      return true;
    });
  };

  const steps = getWorkflowSteps();
  
  // Determine which steps are relevant and completed
  const getRelevantSteps = () => {
    const relevant = [];
    
    steps.forEach(step => {
      let shouldShow = false;
      let isCompleted = false;
      
      if (step.status === 'SCHEDULED') {
        shouldShow = true;
        isCompleted = true; // Always completed if WO exists
      } else if (step.checkField) {
        if (step.conditional) {
          // Conditional steps (Store, MRO) - only show if needed
          if (step.checkField === 'needsStoreAction') {
            shouldShow = workOrder?.needsStoreAction || false;
            isCompleted = shouldShow && !needsStoreIntentAction(workOrder);
          } else if (step.status === 'PLANNING_RESCHEDULE_AFTER_STORE') {
            const plannedDate = workOrder?.scheduledDate ? new Date(workOrder.scheduledDate) : null;
            const availableFrom = workOrder?.storeAvailabilityDate ? new Date(workOrder.storeAvailabilityDate) : null;
            shouldShow = Boolean(
              workOrder?.storeRejectedAt &&
              workOrder?.storeAvailabilityDate &&
              !needsStoreIntentAction(workOrder)
            );
            isCompleted = Boolean(
              plannedDate &&
              availableFrom &&
              !Number.isNaN(plannedDate.getTime()) &&
              !Number.isNaN(availableFrom.getTime()) &&
              plannedDate.toISOString().slice(0, 10) > availableFrom.toISOString().slice(0, 10)
            );
          } else if (step.checkField === 'needsMROAction') {
            shouldShow = workOrder?.needsMROAction || false;
            isCompleted = workOrder?.mroAccepted || workOrder?.mroRejectedAt ? true : false;
          } else if (step.checkField === 'needsExternalAgencyAction') {
            shouldShow = workOrder?.needsExternalAgencyAction || false;
            isCompleted = Boolean(workOrder?.externalAgencyCompleted);
          }
        } else {
          // Required steps
          shouldShow = true;
          if (step.checkField === 'camoQmAcknowledged') {
            isCompleted = Boolean(workOrder?.camoQmAcknowledged);
          } else {
            isCompleted = workOrder?.[step.checkField] || false;
          }
        }
      } else {
        // Status-based steps
        shouldShow = true;
        if (step.status === 'IN_PROGRESS') {
          isCompleted = currentStatus === 'IN_PROGRESS' || currentStatus === 'COMPLETED';
        } else if (step.status === 'COMPLETED') {
          isCompleted = currentStatus === 'COMPLETED';
        }
      }
      
      if (shouldShow) {
        relevant.push({ ...step, completed: isCompleted });
      }
    });
    
    return relevant;
  };

  const relevantSteps = getRelevantSteps();
  const rejectionContext = (() => {
    if (workOrder?.camoQmRejectedAt && !workOrder?.camoQmAcknowledged) {
      return {
        stepStatus: 'CAMO_QM_ACKNOWLEDGED',
        rollbackTo: 'SCHEDULED',
        message: 'CAMO_QM rejected Step 2. Workflow moved back to Step 1.',
        remarks: workOrder?.camoQmRejectionRemarks
      };
    }
    if (workOrder?.planningRejectedAt && !workOrder?.planningApproved) {
      return {
        stepStatus: 'PLANNING_APPROVED',
        rollbackTo: 'CAMO_QM_ACKNOWLEDGED',
        message: 'Planning rejected Step 3. Workflow moved back to Step 2.',
        remarks: workOrder?.planningRejectionRemarks
      };
    }
    if (workOrder?.mroRejectedAt) {
      return {
        stepStatus: 'MRO_ACCEPTED',
        rollbackTo: 'PLANNING_APPROVED',
        message: 'MRO rejected Step 6. Workflow moved back to Step 3.',
        remarks: workOrder?.mroRejectionNotes
      };
    }
    if (workOrder?.mroEngineerRejectedAt && !workOrder?.mroEngineerCompleted) {
      return {
        stepStatus: 'MRO_ENGINEER_COMPLETED',
        rollbackTo: 'MRO_ACCEPTED',
        message: 'MRO_ENGINEER rejected Step 7. Workflow moved back to Step 6.',
        remarks: workOrder?.mroEngineerRejectionRemarks
      };
    }
    if (workOrder?.mroQmCompletionRejectedAt && !workOrder?.mroQmCompletionAcknowledged && workOrder?.mroEngineerCompleted) {
      return {
        stepStatus: 'MRO_QM_COMPLETION_ACKNOWLEDGED',
        rollbackTo: 'MRO_QM_COMPLETION_ACKNOWLEDGED',
        message: 'MRO QM rejected Step 8. Work order remains with MRO QM for re-review.',
        remarks: workOrder?.mroQmCompletionRejectionRemarks
      };
    }
    if (workOrder?.mroQmReturnedToEngineerAt && !workOrder?.mroEngineerCompleted) {
      return {
        stepStatus: 'MRO_QM_COMPLETION_ACKNOWLEDGED',
        rollbackTo: 'MRO_ENGINEER_COMPLETED',
        message: 'MRO QM returned Step 8 to Step 7 (MRO engineer).',
        remarks: workOrder?.mroQmReturnedToEngineerRemarks
      };
    }
    if (workOrder?.camoQmCompletionRejectedAt && !workOrder?.camoQmCompletionAcknowledged) {
      return {
        stepStatus: 'CAMO_QM_COMPLETION_ACKNOWLEDGED',
        rollbackTo: 'MRO_QM_COMPLETION_ACKNOWLEDGED',
        message: 'CAMO_QM rejected Step 9. Workflow moved back to Step 8.',
        remarks: workOrder?.camoQmCompletionRejectionRemarks
      };
    }
    return null;
  })();
  const currentStepIndex = relevantSteps.findIndex(step => {
    if (rejectionContext && step.status === rejectionContext.rollbackTo) return true;
    if (step.status === currentStatus) return true;
    if (step.status === 'IN_PROGRESS' && currentStatus === 'IN_PROGRESS') return true;
    if (step.status === 'COMPLETED' && currentStatus === 'COMPLETED') return true;
    if (step.status === 'CAMO_QM_ACKNOWLEDGED' && workOrder?.camoQmAcknowledged && currentStatus === 'SCHEDULED') return true;
    if (step.status === 'PLANNING_APPROVED' && workOrder?.planningApproved && currentStatus === 'SCHEDULED') return true;
    if (step.status === 'MRO_ACCEPTED' && workOrder?.mroAccepted && currentStatus === 'IN_PROGRESS') return true;
    if (step.status === 'MRO_ENGINEER_COMPLETED' && workOrder?.mroEngineerCompleted && currentStatus === 'IN_PROGRESS') return true;
    if (step.status === 'MRO_QM_COMPLETION_ACKNOWLEDGED' && workOrder?.mroQmCompletionAcknowledged && currentStatus === 'IN_PROGRESS') return true;
    if (step.status === 'CAMO_QM_COMPLETION_ACKNOWLEDGED' && workOrder?.camoQmCompletionAcknowledged && currentStatus === 'IN_PROGRESS') return true;
    return false;
  });
  
  const activeStep = currentStepIndex >= 0 ? currentStepIndex : 0;
  const storeAvailabilityNotice = workOrder?.storeRejectedAt && workOrder?.storeAvailabilityDate
    ? [workOrder?.storeRejectionNotes, `Available from ${new Date(workOrder.storeAvailabilityDate).toISOString().slice(0, 10)}`]
        .filter(Boolean)
        .join(' | ')
    : null;

  const getStepStatus = (stepIndex) => {
    const step = relevantSteps[stepIndex];
    if (step.completed) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'pending';
  };

  // Check if status is cancelled or on hold
  const isCancelled = currentStatus === 'CANCELLED';
  const isOnHold = currentStatus === 'HOLD';

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>
        Workflow Progress
      </Typography>
      {rejectionContext && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: 'error.main' }} />
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
              {rejectionContext.message}
            </Typography>
          </Box>
          {rejectionContext.remarks && (
            <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
              Remarks: {rejectionContext.remarks}
            </Typography>
          )}
        </Box>
      )}
      {isOnHold && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PauseIcon sx={{ color: 'warning.main' }} />
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'medium' }}>
              Work Order is on HOLD - Awaiting Parts Procurement
            </Typography>
          </Box>
        </Box>
      )}
      {storeAvailabilityNotice && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PauseIcon sx={{ color: 'warning.main' }} />
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'medium' }}>
              Store set parts availability date. Planning must reschedule after this date before MRO.
            </Typography>
          </Box>
          <Typography variant="caption" color="warning.dark" sx={{ mt: 0.5, display: 'block' }}>
            {storeAvailabilityNotice}
          </Typography>
        </Box>
      )}
      <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 1 }}>
        {relevantSteps.map((step, index) => {
          const stepStatus = getStepStatus(index);
          const isCompleted = stepStatus === 'completed';
          const isActive = stepStatus === 'active';
          const isPending = stepStatus === 'pending';

          return (
            <Step key={step.status} completed={isCompleted} active={isActive}>
              <StepLabel
                error={isCancelled && isActive}
                StepIconComponent={() => {
                  if (isCancelled && isActive) {
                    return <BlockIcon sx={{ color: 'error.main' }} />;
                  } else if (rejectionContext && step.status === rejectionContext.stepStatus) {
                    return <CancelIcon sx={{ color: 'error.main' }} />;
                  } else if (isCompleted) {
                    return <CheckCircleIcon sx={{ color: 'success.main' }} />;
                  } else if (isActive) {
                    return <CheckCircleIcon sx={{ color: 'primary.main' }} />;
                  } else {
                    return <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />;
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: isActive ? 'bold' : 'normal' }}>
                    {step.label}
                  </Typography>
                  {isActive && !isCancelled && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                  {isCompleted && (
                    <Chip
                      label={rejectionContext && step.status === rejectionContext.stepStatus ? 'Rejected' : 'Done'}
                      size="small"
                      color={rejectionContext && step.status === rejectionContext.stepStatus ? 'error' : 'success'}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="caption" color="text.secondary">
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
      {isCancelled && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
          <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
            ⚠ Work Order CANCELLED - Workflow Terminated
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MaintenanceWorkflowProgressIndicator;

